import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  afterRenderEffect,
  computed,
  inject,
  input,
  output,
  signal,
  WritableSignal
} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import {Observable} from 'rxjs';
import * as _ from 'lodash';
import {BzMenuItem} from '@bringoz/ui';
import {
  bzIconCheck,
  bzIconChevronDown,
  bzIconChevronRight,
  bzIconEditOutline,
  bzIconError,
  bzIconGroup,
  bzIconHistoryLog,
  bzIconNoApp,
  bzIconRotate,
  bzIconTrash
} from '@bringoz/ui/icons';
import {Stop} from '../../../types/timecard-detail-view.type';
import {TimeCardStatusType} from '../../../types/timecard-status.type';
import {UpdateSource} from '../../../constants/update-source.constant';
import {TimeCardAnalysis} from '../../models/timecard-analysis.model';
import {
  TimecardEditableField,
  TimecardEntryAnalysisMap,
  TimecardLeave,
  TimecardShift
} from '../../models/timecard-detail.model';

export interface TimecardStopEvent {
  stop: Stop;
  shift: TimecardShift;
}

export interface TimecardEditEntryEvent extends TimecardStopEvent {
  field: TimecardEditableField;
  /** The clicked cell - the edit panel opens under it. */
  anchor: HTMLElement;
}

export interface TimecardHistoryEvent extends TimecardStopEvent {
  field: string;
}

export interface TimecardShiftEvent {
  shift: TimecardShift;
}

export interface TimecardStatusChangeEvent {
  shift: TimecardShift;
  status: TimeCardStatusType;
}

const STATUS_TRANSLATION_KEYS: string[] = _.map(Object.values(TimeCardStatusType), (status) => `TIMECARD_SHIFT_STATUS.${status}`);

/**
 * Shifts of the day with their stops, totals and leaves. Collapse and bundle expansion are view-only state,
 * kept here by id so they survive reloads of the day.
 */
require('./timecard-shift-list.scss');
@Component({
  standalone: false,
  selector: 'app-timecard-shift-list',
  templateUrl: './timecard-shift-list.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimecardShiftListComponent {

  readonly shifts = input<TimecardShift[]>([]);
  readonly leaves = input<TimecardLeave[]>([]);
  readonly selectedShiftId = input<string>();
  readonly selectedTaskId = input<string>();
  readonly analysisMap = input<Record<string, TimeCardAnalysis>>({});
  readonly entryAnalysisMap = input<TimecardEntryAnalysisMap>({});
  readonly statusHistoryShiftIds = input<Set<string>>(new Set());
  readonly siteDistanceUnit = input<string>();
  readonly configTimeZone = input<string>();
  readonly isStatusChanging = input<boolean>(false);
  readonly isRefreshingData = input<boolean>(false);
  readonly serviceHrefs = input<Record<string, string>>({});

  readonly shiftSelect = output<string>();
  readonly stopSelect = output<{ stop: Stop; shiftId: string }>();
  readonly editEntry = output<TimecardEditEntryEvent>();
  readonly openHistory = output<TimecardHistoryEvent>();
  readonly openStatusHistory = output<TimecardShiftEvent>();
  readonly changeStatus = output<TimecardStatusChangeEvent>();
  readonly refreshData = output<TimecardShiftEvent>();
  readonly archiveStop = output<TimecardStopEvent>();

  readonly collapsedIds = signal<ReadonlySet<string>>(new Set());
  readonly expandedBundleIds = signal<ReadonlySet<string>>(new Set());

  readonly UpdateSource = UpdateSource;
  readonly TimeCardStatusType = TimeCardStatusType;
  readonly icons = {
    expanded: bzIconChevronDown,
    collapsed: bzIconChevronRight,
    noApp: bzIconNoApp,
    risk: bzIconError,
    refresh: bzIconRotate,
    history: bzIconHistoryLog,
    edit: bzIconEditOutline,
    archive: bzIconTrash,
    bundle: bzIconGroup,
    check: bzIconCheck
  };

  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly statusLabels = toSignal(
    inject(TranslateService).stream(STATUS_TRANSLATION_KEYS) as Observable<Record<string, string>>,
    {initialValue: {} as Record<string, string>}
  );

  /** Status menu items of every shift, by shift id. */
  readonly statusMenuItems = computed<Record<string, BzMenuItem[]>>(() => {
    const labels = this.statusLabels();
    return _.fromPairs(this.shifts().map((shift) => [
      shift.id,
      shift.statusOptions.map((status) => ({id: status, label: labels[`TIMECARD_SHIFT_STATUS.${status}`] || status}))
    ]));
  });

  constructor() {
    // Status cells hold badges of different widths; they share the widest one so the data columns stay in line.
    afterRenderEffect(() => {
      this.shifts();
      this.analysisMap();
      this.statusHistoryShiftIds();
      this.alignStatusCells();
    });
  }

  onShiftHeaderClick(shift: TimecardShift, event: MouseEvent) {
    event.stopPropagation();
    this.shiftSelect.emit(shift.id);
    this.toggleId(this.collapsedIds, shift.id);
  }

  onLeaveHeaderClick(leave: TimecardLeave, event: MouseEvent) {
    event.stopPropagation();
    this.toggleId(this.collapsedIds, leave.id);
  }

  onToggleBundle(stop: Stop, event: MouseEvent) {
    event.stopPropagation();
    this.toggleId(this.expandedBundleIds, stop.id);
  }

  onEditEntry(event: MouseEvent, stop: Stop, shift: TimecardShift, field: TimecardEditableField, isEditable: boolean) {
    if (!isEditable) {
      return;
    }
    event.stopPropagation();
    this.editEntry.emit({stop, shift, field, anchor: event.currentTarget as HTMLElement});
  }

  onOpenHistory(event: MouseEvent, stop: Stop, shift: TimecardShift, field: string) {
    event.stopPropagation();
    this.openHistory.emit({stop, shift, field});
  }

  onOpenStatusHistory(event: MouseEvent, shift: TimecardShift) {
    event.stopPropagation();
    this.openStatusHistory.emit({shift});
  }

  onStatusSelected(shift: TimecardShift, item: BzMenuItem) {
    this.changeStatus.emit({shift, status: item.id as TimeCardStatusType});
  }

  onRefresh(event: MouseEvent, shift: TimecardShift) {
    event.stopPropagation();
    this.refreshData.emit({shift});
  }

  onArchive(event: MouseEvent, stop: Stop, shift: TimecardShift) {
    event.stopPropagation();
    this.archiveStop.emit({stop, shift});
  }

  private toggleId(target: WritableSignal<ReadonlySet<string>>, id: string) {
    target.update((ids) => {
      const next = new Set(ids);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  private alignStatusCells() {
    const cells = Array.from(this.elementRef.nativeElement.querySelectorAll<HTMLElement>('.status-cell'));
    if (!cells.length) {
      return;
    }
    cells.forEach((cell) => {
      cell.style.minWidth = '';
      cell.style.width = '';
    });
    const maxWidth = Math.max(...cells.map((cell) => cell.scrollWidth));
    if (maxWidth > 0) {
      cells.forEach((cell) => cell.style.minWidth = `${maxWidth}px`);
    }
  }
}
